import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const HIDDEN_PATHS = [
  "/entrar", "/register", "/cadastro", "/redefinir-senha", "/esqueci-minha-senha",
  "/trocar-senha", "/seguranca", "/admin", "/consultor", "/clinica", "/profissional",
  "/dashboard", "/familia", "/emergencia", "/pagamento", "/consulta", "/triagem", "/caso",
  "/plano-familiar", "/proposta", "/meus-atendimentos", "/pedir-ajuda", "/perfil",
];
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const RAF_PHONE = "5511996420785";
const CENTRAL_PRODUCTION_ENABLED = process.env.REACT_APP_CENTRAL_PRODUCTION_ENABLED === "true";

export default function LiveHelpWidget() {
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const [pathname, setPathname] = useState(typeof window !== "undefined" ? window.location.pathname : "/");
  const [session, setSession] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("raf_central_session") || "null"); } catch { return null; }
  });
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const shouldShow = CENTRAL_PRODUCTION_ENABLED && !loading && !user && !HIDDEN_PATHS.some(p => pathname === p || pathname.startsWith(p + "/"));

  useEffect(() => {
    const iv = setInterval(() => { if (window.location.pathname !== pathname) setPathname(window.location.pathname); }, 700);
    return () => clearInterval(iv);
  }, [pathname]);

  // v0.14 — Auto-abre widget quando família chega vindo de um CTA do blog
  useEffect(() => {
    if (!shouldShow) return;
    try {
      if (sessionStorage.getItem("lia_auto_open") === "1") {
        sessionStorage.removeItem("lia_auto_open");
        setTimeout(() => { openChat(); }, 400);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow]);

  // v0.17 — Nudge da Home dispara `raf:lia-open` pra abrir o widget na hora
  useEffect(() => {
    const handler = () => { if (shouldShow) openChat(); };
    window.addEventListener("raf:lia-open", handler);
    return () => window.removeEventListener("raf:lia-open", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow, session]);

  const start = async () => {
    if (session) return session;
    const r = await fetch(`${BACKEND}/api/messaging/site/start`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ page_url: window.location.href, environment: "production" }),
    });
    if (!r.ok) throw new Error("Não foi possível iniciar o atendimento");
    const data = await r.json();
    const s = { conversation_id: data.conversation_id, visitor_token: data.visitor_token };
    sessionStorage.setItem("raf_central_session", JSON.stringify(s));
    setSession(s); setMessages(data.messages || []);
    // Google Ads — conversão "form_send" no início da triagem LIA (leva o
    // custo por lead pra dashboard do Google Ads). Idempotente na sessão.
    try {
      if (!sessionStorage.getItem("raf_ads_form_sent")) {
        sessionStorage.setItem("raf_ads_form_sent", "1");
        const mod = await import("@/lib/adsTracking");
        mod.trackAdsConversion("form_send", { source: "lia_widget_start" });
      }
    } catch { /* silent */ }
    return s;
  };

  const openChat = async () => {
    setOpen(true);
    try { await start(); } catch { /* fallback WhatsApp fica visível */ }
  };

  const reload = async (s = session) => {
    if (!s) return;
    try {
      const url = `${BACKEND}/api/messaging/site/${s.conversation_id}/messages?visitor_token=${encodeURIComponent(s.visitor_token)}`;
      const r = await fetch(url);
      if (r.ok) { const d = await r.json(); setMessages(d.messages || []); }
    } catch { /* silencioso */ }
  };

  useEffect(() => {
    if (!open || !session) return;
    reload();
    const iv = setInterval(() => reload(), 5000);
    return () => clearInterval(iv);
  }, [open, session]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  // Iter 68.68 — Atualiza contexto da LIA quando família navega para outra página pública
  useEffect(() => {
    if (!session) return;
    let last = window.location.pathname;
    const check = async () => {
      if (window.location.pathname === last) return;
      last = window.location.pathname;
      try {
        await fetch(`${BACKEND}/api/messaging/site/${session.conversation_id}/refresh-context`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ visitor_token: session.visitor_token, page_url: window.location.href }),
        });
        await reload(session);
      } catch { /* silencioso */ }
    };
    const iv = setInterval(check, 1500);
    return () => clearInterval(iv);
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (value = text) => {
    const msg = value.trim(); if (!msg || sending) return;
    setSending(true);
    try {
      const s = session || await start();
      const r = await fetch(`${BACKEND}/api/messaging/site/${s.conversation_id}/messages`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visitor_token: s.visitor_token, text: msg }),
      });
      if (!r.ok) throw new Error();
      setText(""); await reload(s);
    } finally { setSending(false); }
  };

  if (!shouldShow) return null;
  const wa = `https://wa.me/${RAF_PHONE}?text=${encodeURIComponent("Olá! Estou no site da RAF Direct e gostaria de atendimento.")}`;

  return <div className="fixed bottom-5 right-5 z-[9998] flex flex-col items-end gap-2">
    {open && <div className="w-[350px] sm:w-[390px] h-[570px] max-h-[75vh] rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden flex flex-col" data-testid="raf-site-chat">
      <div className="bg-[#0B2B62] text-white p-4 flex items-start justify-between">
        <div><div className="text-[10px] uppercase tracking-[0.16em] text-blue-200">Central RAF Direct</div><div className="font-bold mt-0.5">LIA — Inteligência de Acolhimento</div><div className="text-xs text-blue-100 mt-1">LIA agora · atendente humano quando necessário</div></div>
        <button onClick={()=>setOpen(false)} className="p-1.5 hover:bg-white/10 rounded"><X className="w-4 h-4"/></button>
      </div>
      <div className="px-3 py-2 bg-amber-50 border-b text-[11px] text-amber-800">Não usamos este chat para diagnóstico. Em situações de emergência, procure atendimento de urgência da sua região.</div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
        {messages.map(m=>{ const visitor=m.sender_type==="visitor"; const system=m.sender_type==="system"; if(system)return <div key={m.id} className="text-center text-[10px] text-slate-400">{m.text}</div>; const waCta = (m.attachments||[]).find(a=>a.kind==="cta_whatsapp"); return <div key={m.id} className={`flex ${visitor?"justify-end":"justify-start"}`}><div className={`max-w-[88%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${visitor?"bg-[#0B2B62] text-white":"bg-white border border-slate-200 text-slate-700"}`}>{m.text}{waCta && (<a href={waCta.url} target="_blank" rel="noreferrer" data-testid="lia-wa-cta" className="mt-3 flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm shadow transition"><MessageCircle className="w-5 h-5"/> {waCta.label || "Abrir WhatsApp"}</a>)}</div></div>})}
        {!messages.length && <div className="text-sm text-slate-500 p-4">Iniciando atendimento...</div>}
        <div ref={endRef}/>
      </div>
      <div className="p-3 border-t bg-white">
        <div className="flex gap-2"><input value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()} placeholder="Digite sua resposta..." className="flex-1 border rounded-xl px-3 py-2.5 text-sm"/><button onClick={()=>send()} disabled={!text.trim()||sending} className="w-11 rounded-xl bg-[#0B2B62] text-white flex items-center justify-center disabled:opacity-40"><Send className="w-4 h-4"/></button></div>
        <div className="flex items-center justify-between mt-2"><button onClick={()=>send("Quero falar com atendente humano")} className="text-[11px] font-semibold text-[#0B2B62]">Falar com atendente</button><a href={wa} target="_blank" rel="noreferrer" className="text-[11px] text-emerald-700 inline-flex items-center gap-1">Abrir WhatsApp <ExternalLink className="w-3 h-3"/></a></div>
      </div>
    </div>}
    <button onClick={openChat} className="group relative w-14 h-14 rounded-full bg-[#0B2B62] hover:bg-blue-900 text-white shadow-2xl flex items-center justify-center transition-transform hover:scale-105" aria-label="Abrir Central de Atendimento" data-testid="live-help-bubble"><MessageCircle className="w-6 h-6"/><span className="absolute top-0 right-0 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-white animate-pulse"/></button>
  </div>;
}
